<script setup>
import { computed, onMounted, ref } from 'vue';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import googleDriveIntegrationAPI from 'dashboard/api/googleDriveIntegration';

const store = useStore();
const { t } = useI18n();
const integrationLoaded = ref(false);
const loadingFolders = ref(false);
const saving = ref(false);
const disconnecting = ref(false);

const integration = useFunctionGetter(
  'integrations/getIntegration',
  'google_drive',
  {}
);
const uiFlags = useMapGetter('integrations/getUIFlags');
const integrationData = computed(() => integration.value || {});
const isFetchingIntegrations = computed(() => uiFlags.value?.isFetching ?? false);

const credentialsJson = ref('');
const folderQuery = ref('');
const folders = ref([]);
const selectedFolderId = ref('');
const selectedFolderName = ref('');
const hasSavedCredentials = ref(false);

const hasFolderSelection = computed(() => selectedFolderId.value !== '');

const loadIntegrationState = async () => {
  const { data } = await googleDriveIntegrationAPI.show();
  hasSavedCredentials.value = data.has_credentials;
  selectedFolderId.value = data.folder_id || '';
  selectedFolderName.value = data.folder_name || '';
};

const initialize = async () => {
  try {
    await store.dispatch('integrations/get', 'google_drive');
    await loadIntegrationState();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.LOAD_ERROR')
    );
  } finally {
    integrationLoaded.value = true;
  }
};

const loadFolders = async () => {
  loadingFolders.value = true;
  try {
    const params = {};
    if (credentialsJson.value.trim()) {
      params.credentials_json = credentialsJson.value.trim();
    }
    if (folderQuery.value.trim()) {
      params.query = folderQuery.value.trim();
    }

    const { data } = await googleDriveIntegrationAPI.listFolders(params);
    folders.value = data.folders || [];
    if (!folders.value.length) {
      useAlert(t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.FOLDERS.EMPTY'));
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.FOLDERS.ERROR')
    );
  } finally {
    loadingFolders.value = false;
  }
};

const saveSettings = async () => {
  if (!hasFolderSelection.value) {
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.FOLDER_REQUIRED'));
    return;
  }

  saving.value = true;
  try {
    const payload = { folder_id: selectedFolderId.value };
    if (credentialsJson.value.trim()) {
      payload.credentials_json = credentialsJson.value.trim();
    }

    const { data } = await googleDriveIntegrationAPI.update(payload);
    selectedFolderName.value = data.folder_name || selectedFolderName.value;
    hasSavedCredentials.value = data.has_credentials;
    credentialsJson.value = '';
    await store.dispatch('integrations/get');
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const disconnect = async () => {
  disconnecting.value = true;
  try {
    await googleDriveIntegrationAPI.disconnect();
    credentialsJson.value = '';
    folderQuery.value = '';
    folders.value = [];
    selectedFolderId.value = '';
    selectedFolderName.value = '';
    hasSavedCredentials.value = false;
    await store.dispatch('integrations/get');
    useAlert(t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.DISCONNECT_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.DISCONNECT_ERROR')
    );
  } finally {
    disconnecting.value = false;
  }
};

onMounted(() => {
  initialize();
});
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto max-w-5xl mx-auto">
    <div v-if="integrationLoaded && !isFetchingIntegrations" class="space-y-6">
      <div class="rounded-xl border bg-white p-6 shadow-sm">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="flex items-start gap-4">
            <img
              :src="'/dashboard/images/integrations/google-drive.png'"
              class="h-12 w-12 rounded-md border border-n-weak"
            />
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                {{
                  integrationData.name ||
                  t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.DEFAULT_NAME')
                }}
              </h2>
              <p class="text-sm text-n-slate-11 mt-1">
                {{
                  integrationData.description ||
                  t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.DEFAULT_DESCRIPTION')
                }}
              </p>
              <p class="text-xs text-n-slate-10 mt-2">
                {{ t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.STATUS_LABEL') }}:
                <span class="font-medium">{{ integrationData.enabled ? t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.STATUS_CONNECTED') : t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.STATUS_DISCONNECTED') }}</span>
              </p>
            </div>
          </div>
          <Button
            v-if="integrationData.enabled"
            faded
            ruby
            :label="
              disconnecting
                ? t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.DISCONNECTING')
                : t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.DISCONNECT')
            "
            :is-loading="disconnecting"
            @click="disconnect"
          />
        </div>
      </div>

      <div class="rounded-xl border bg-white p-6 shadow-sm space-y-5">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.STEP_CREDENTIALS') }}
          </h3>
          <p class="text-sm text-n-slate-11 mt-1">
            {{ t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.CREDENTIALS_HELP') }}
          </p>
          <p v-if="hasSavedCredentials" class="text-xs text-n-teal-9 mt-2">
            {{ t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.CREDENTIALS_SAVED') }}
          </p>
        </div>

        <textarea
          v-model="credentialsJson"
          rows="8"
          :placeholder="t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.CREDENTIALS_PLACEHOLDER')"
          class="w-full border rounded-lg p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-woot-500/20"
        />

        <div class="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
          <div>
            <label class="text-sm font-medium text-n-slate-12">{{
              t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.SEARCH_LABEL')
            }}</label>
            <input
              v-model="folderQuery"
              type="text"
              :placeholder="
                t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.SEARCH_PLACEHOLDER')
              "
              class="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/20"
            />
          </div>
          <Button
            blue
            :label="
              loadingFolders
                ? t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.LOADING_FOLDERS')
                : t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.LOAD_FOLDERS')
            "
            :is-loading="loadingFolders"
            @click="loadFolders"
          />
        </div>

        <div>
          <label class="text-sm font-medium text-n-slate-12">{{
            t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.STEP_FOLDER')
          }}</label>
          <select
            v-model="selectedFolderId"
            class="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-woot-500/20"
          >
            <option value="">{{
              t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.FOLDER_PLACEHOLDER')
            }}</option>
            <option v-for="folder in folders" :key="folder.id" :value="folder.id">
              {{ folder.name }}
            </option>
          </select>
          <p v-if="selectedFolderName" class="text-xs text-n-slate-10 mt-2">
            {{ t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.CURRENT_FOLDER') }}:
            {{ selectedFolderName }}
          </p>
        </div>

        <div class="flex flex-wrap gap-2">
          <Button
            teal
            :label="
              saving
                ? t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.SAVING')
                : t('INTEGRATION_SETTINGS.GOOGLE_DRIVE.SAVE')
            "
            :is-loading="saving"
            @click="saveSettings"
          />
        </div>
      </div>
    </div>

    <div v-else class="flex items-center justify-center flex-1">
      <Spinner size="" color-scheme="primary" />
    </div>
  </div>
</template>
